import { useEffect, useState } from 'react'
import { Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { usePlanUndo } from '@/hooks/usePlanUndo'

interface PlanUndoFabProps {
  planId: string
  versionId: string | undefined
  disabled?: boolean
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  )
}

/**
 * Floating "Undo" button for the plan detail page. Hidden until the user
 * makes a change during the current session; also triggered by Ctrl/Cmd+Z.
 */
export function PlanUndoFab({
  planId,
  versionId,
  disabled = false,
}: PlanUndoFabProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { topEntry, hasSessionChanges, isPending, undo } = usePlanUndo(
    planId,
    versionId
  )
  const visible = hasSessionChanges && !disabled

  useEffect(() => {
    if (!visible) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !(e.metaKey || e.ctrlKey) ||
        e.shiftKey ||
        e.altKey ||
        e.key.toLowerCase() !== 'z' ||
        isEditableTarget(e.target) ||
        isPending
      ) {
        return
      }
      e.preventDefault()
      setDialogOpen(true)
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [visible, isPending])

  const handleConfirmUndo = () => {
    undo(undefined, {
      onSettled: () => setDialogOpen(false),
    })
  }

  return (
    <>
      {visible && (
        <Button
          className="fixed bottom-6 right-6 z-40 gap-2 shadow-lg bg-[#CF1F2E] hover:bg-[#b81a27] animate-in fade-in-0 slide-in-from-bottom-2 duration-200"
          onClick={() => setDialogOpen(true)}
          disabled={isPending}
          title={topEntry?.description}
        >
          <Undo2 className="h-4 w-4" />
          Undo
        </Button>
      )}

      <ConfirmationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Undo Last Change"
        description={`Are you sure you want to undo: "${topEntry?.description}"? This cannot be redone.`}
        confirmText="Undo"
        cancelText="Cancel"
        onConfirm={handleConfirmUndo}
        variant="destructive"
        isLoading={isPending}
      />
    </>
  )
}
